import React, { useState, useEffect } from 'react';
import TeamMemberService, { TeamMember } from '../../../services/TeamMemberService';
import PracticeRolesService, {
  PracticeRole,
  PermissionKey,
  PERMISSION_LABELS,
  ALL_PERMISSIONS
} from '../../../services/PracticeRolesService';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../Toast';

const RolesAccess: React.FC = () => {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [roles, setRoles] = useState<PracticeRole[]>([]);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const practiceOwnerId = user?.uid || '';
  const rolesService = PracticeRolesService.getInstance();

  useEffect(() => {
    if (!practiceOwnerId) return;

    // Initialize roles service
    rolesService.initialize(practiceOwnerId);

    const handleRolesUpdate = () => setRoles(rolesService.getRoles());
    rolesService.subscribe(handleRolesUpdate);
    setRoles(rolesService.getRoles());

    // Team members
    const teamService = TeamMemberService.getInstance();
    setTeamMembers(teamService.getAllTeamMembers());
    const handleTeamUpdate = () => setTeamMembers(teamService.getAllTeamMembers());
    teamService.subscribe(handleTeamUpdate);

    return () => {
      rolesService.unsubscribe(handleRolesUpdate);
      teamService.unsubscribe(handleTeamUpdate);
    };
  }, [practiceOwnerId]);

  const togglePermission = async (roleId: string, perm: PermissionKey) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    const updated = {
      ...role.permissions,
      [perm]: !role.permissions[perm],
    };

    setSaving(true);
    try {
      await rolesService.updateRolePermissions(roleId, updated);
    } catch (err: any) {
      toast.error('Error', `Failed to update permission: ${err.message}`);
    }
    setSaving(false);
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;

    const id = newRoleName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');

    if (roles.some(r => r.id === id)) {
      toast.error('Error', 'A role with that name already exists');
      return;
    }

    // Random color from a nice palette
    const colors = ['#8B5CF6', '#EC4899', '#F97316', '#14B8A6', '#6366F1', '#84CC16', '#F43F5E', '#06B6D4'];
    const color = colors[Math.floor(Math.random() * colors.length)];

    // Start with no permissions (practice checks what they want)
    const permissions: Record<PermissionKey, boolean> = {} as Record<PermissionKey, boolean>;
    ALL_PERMISSIONS.forEach(p => { permissions[p] = false; });

    setSaving(true);
    try {
      await rolesService.addRole({
        id,
        name: newRoleName.trim(),
        description: newRoleDesc.trim() || 'Custom role',
        color,
        permissions,
      });
      toast.success('Role Created', `${newRoleName.trim()} has been added`);
      setShowCreateModal(false);
      setNewRoleName('');
      setNewRoleDesc('');
    } catch (err: any) {
      toast.error('Error', `Failed to create role: ${err.message}`);
    }
    setSaving(false);
  };

  const handleDeleteRole = async (roleId: string) => {
    const membersWithRole = teamMembers.filter(m => {
      const normalized = m.role.toLowerCase().replace(/[^a-z0-9]+/g, '_');
      return normalized === roleId || m.role === roleId;
    });

    if (membersWithRole.length > 0) {
      toast.error('Cannot Delete', `${membersWithRole.length} team member(s) are assigned to this role. Reassign them first.`);
      setDeleteConfirm(null);
      return;
    }

    setSaving(true);
    try {
      await rolesService.deleteRole(roleId);
      toast.success('Role Deleted', 'Custom role has been removed');
    } catch (err: any) {
      toast.error('Error', `Failed to delete role: ${err.message}`);
    }
    setDeleteConfirm(null);
    setSaving(false);
  };

  const handleSaveEdit = async (roleId: string) => {
    setSaving(true);
    try {
      await rolesService.updateRoleMeta(roleId, editName.trim(), editDesc.trim());
      toast.success('Updated', 'Role name and description updated');
      setEditingRole(null);
    } catch (err: any) {
      toast.error('Error', `Failed to update: ${err.message}`);
    }
    setSaving(false);
  };

  const startEditing = (role: PracticeRole) => {
    setEditingRole(role.id);
    setEditName(role.name);
    setEditDesc(role.description);
  };

  const getMembersForRole = (roleId: string): TeamMember[] => {
    return teamMembers.filter(m => {
      const normalized = m.role.toLowerCase().replace(/[^a-z0-9]+/g, '_');
      return normalized === roleId || m.role === roleId;
    });
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1100px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '600', color: '#F7E9A0', margin: '0 0 8px 0' }}>
            Roles & Access Control
          </h1>
          <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0 }}>
            Customize permissions for each role. Toggle checkboxes to control access. All staff can always access their own HR documents.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={saving}
          style={{
            padding: '10px 20px',
            backgroundColor: '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          + Create Role
        </button>
      </div>

      {/* Info Banner */}
      <div style={{
        backgroundColor: 'rgba(16,185,129,0.1)',
        border: '1px solid rgba(16,185,129,0.3)',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '24px',
        fontSize: '13px',
        color: '#6EE7B7',
      }}>
        <strong>Own Documents Always Granted:</strong> Unchecking "HR Documents (All Staff)" only removes access to other staff members' documents.
        Every team member can always view and manage their own HR files.
      </div>

      {/* Role Cards with Permissions */}
      {roles.map(role => {
        const members = getMembersForRole(role.id);
        return (
          <div key={role.id} style={{
            backgroundColor: '#28527A',
            borderRadius: '8px',
            border: `1px solid ${role.color}40`,
            marginBottom: '16px',
            overflow: 'hidden',
          }}>
            {/* Role Header */}
            <div style={{
              padding: '16px 20px',
              backgroundColor: '#1f2937',
              borderBottom: `1px solid ${role.color}40`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '12px', height: '12px', borderRadius: '50%',
                  backgroundColor: role.color,
                }} />
                {editingRole === role.id ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      style={{
                        backgroundColor: '#374151', border: '1px solid #6CA6C1', borderRadius: '4px',
                        color: 'white', padding: '4px 8px', fontSize: '14px', width: '200px',
                      }}
                    />
                    <input
                      value={editDesc}
                      onChange={e => setEditDesc(e.target.value)}
                      placeholder="Description"
                      style={{
                        backgroundColor: '#374151', border: '1px solid #6CA6C1', borderRadius: '4px',
                        color: '#9CA3AF', padding: '4px 8px', fontSize: '12px', width: '250px',
                      }}
                    />
                    <button
                      onClick={() => handleSaveEdit(role.id)}
                      style={{ backgroundColor: '#10B981', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingRole(null)}
                      style={{ backgroundColor: '#4B5563', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div>
                    <span style={{ color: '#F7E9A0', fontWeight: '600', fontSize: '16px' }}>
                      {role.name}
                    </span>
                    {role.isDefault && (
                      <span style={{
                        marginLeft: '8px', fontSize: '10px', color: '#9CA3AF',
                        backgroundColor: '#374151', padding: '2px 6px', borderRadius: '4px',
                      }}>
                        DEFAULT
                      </span>
                    )}
                    <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '2px 0 0 0' }}>
                      {role.description}
                    </p>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {members.length > 0 && (
                  <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                    {members.length} member{members.length !== 1 ? 's' : ''}
                  </span>
                )}
                {!role.isDefault && editingRole !== role.id && (
                  <>
                    <button
                      onClick={() => startEditing(role)}
                      style={{
                        backgroundColor: '#374151', color: '#93C5FD', border: 'none',
                        borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                    {deleteConfirm === role.id ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={() => handleDeleteRole(role.id)}
                          style={{
                            backgroundColor: '#EF4444', color: 'white', border: 'none',
                            borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
                          }}
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          style={{
                            backgroundColor: '#4B5563', color: 'white', border: 'none',
                            borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(role.id)}
                        style={{
                          backgroundColor: '#374151', color: '#FCA5A5', border: 'none',
                          borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Permission Checkboxes Grid */}
            <div style={{
              padding: '16px 20px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '8px',
            }}>
              {ALL_PERMISSIONS.map(perm => (
                <label
                  key={perm}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    backgroundColor: role.permissions[perm] ? 'rgba(16,185,129,0.1)' : 'rgba(75,85,99,0.2)',
                    border: `1px solid ${role.permissions[perm] ? 'rgba(16,185,129,0.3)' : '#374151'}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    fontSize: '13px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={role.permissions[perm] || false}
                    onChange={() => togglePermission(role.id, perm)}
                    disabled={saving}
                    style={{
                      width: '16px', height: '16px',
                      accentColor: '#10B981',
                      cursor: 'pointer',
                    }}
                  />
                  <span style={{ color: role.permissions[perm] ? '#E5E7EB' : '#9CA3AF' }}>
                    {PERMISSION_LABELS[perm]}
                  </span>
                </label>
              ))}
              {/* Always-on indicator */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: 'rgba(16,185,129,0.05)',
                border: '1px solid rgba(16,185,129,0.15)',
                fontSize: '13px',
              }}>
                <span style={{ color: '#10B981', fontSize: '14px' }}>&#10003;</span>
                <span style={{ color: '#6EE7B7', fontStyle: 'italic' }}>Own HR Documents (always on)</span>
              </div>
            </div>

            {/* Members Assigned */}
            {members.length > 0 && (
              <div style={{
                padding: '8px 20px 12px',
                borderTop: '1px solid #374151',
                display: 'flex',
                gap: '6px',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}>
                <span style={{ fontSize: '11px', color: '#9CA3AF', marginRight: '4px' }}>Assigned:</span>
                {members.map(m => (
                  <span key={m.id} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: '#374151',
                    fontSize: '11px',
                    color: '#E5E7EB',
                  }}>
                    <span style={{
                      width: '6px', height: '6px', borderRadius: '50%',
                      backgroundColor: m.status === 'active' ? '#10B981' : '#F59E0B',
                    }} />
                    {m.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Team Member Role Assignments Table */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        border: '1px solid #6CA6C1',
        overflow: 'hidden',
        marginTop: '24px',
      }}>
        <div style={{
          padding: '16px 20px',
          backgroundColor: '#1f2937',
          borderBottom: '1px solid #6CA6C1',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#F7E9A0', margin: 0 }}>
            Team Member Assignments
          </h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 2fr 1fr',
          gap: '16px',
          padding: '12px 20px',
          backgroundColor: '#1a2332',
          borderBottom: '1px solid #374151',
          fontSize: '13px',
          fontWeight: '600',
          color: '#9CA3AF',
        }}>
          <div>Team Member</div>
          <div>Role</div>
          <div>Status</div>
        </div>
        {teamMembers.map(member => {
          const roleObj = roles.find(r => {
            const normalized = member.role.toLowerCase().replace(/[^a-z0-9]+/g, '_');
            return r.id === normalized || r.id === member.role;
          });
          return (
            <div key={member.id} style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 1fr',
              gap: '16px',
              padding: '14px 20px',
              alignItems: 'center',
              borderBottom: '1px solid #374151',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  backgroundColor: member.avatarColor || '#6CA6C1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: '13px', fontWeight: 'bold',
                }}>
                  {member.initials || member.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div style={{ color: '#F9FAFB', fontSize: '14px', fontWeight: '500' }}>{member.name}</div>
                  <div style={{ color: '#9CA3AF', fontSize: '12px' }}>{member.email}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  backgroundColor: roleObj?.color || '#6CA6C1',
                }} />
                <span style={{ color: '#F9FAFB', fontSize: '14px' }}>{roleObj?.name || member.role}</span>
              </div>
              <div>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: member.status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                  color: member.status === 'active' ? '#6EE7B7' : '#FBBF24',
                }}>
                  {member.status === 'active' ? 'Active' : 'Invited'}
                </span>
              </div>
            </div>
          );
        })}
        {teamMembers.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
            No team members yet. Add team members from the Team Members section.
          </div>
        )}
      </div>

      {/* Create Role Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setShowCreateModal(false)}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#1f2937',
              borderRadius: '12px',
              border: '1px solid #6CA6C1',
              padding: '32px',
              width: '440px',
              maxWidth: '90vw',
            }}
          >
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#F7E9A0', margin: '0 0 20px 0' }}>
              Create New Role
            </h2>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#9CA3AF', marginBottom: '6px' }}>
                Role Name *
              </label>
              <input
                value={newRoleName}
                onChange={e => setNewRoleName(e.target.value)}
                placeholder="e.g. HR Manager, Intern, Front Desk"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '6px',
                  backgroundColor: '#374151', border: '1px solid #6CA6C1',
                  color: 'white', fontSize: '14px', boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#9CA3AF', marginBottom: '6px' }}>
                Description
              </label>
              <input
                value={newRoleDesc}
                onChange={e => setNewRoleDesc(e.target.value)}
                placeholder="Brief description of this role"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '6px',
                  backgroundColor: '#374151', border: '1px solid #6CA6C1',
                  color: 'white', fontSize: '14px', boxSizing: 'border-box',
                }}
              />
            </div>
            <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '0 0 20px 0' }}>
              The new role will start with no permissions. You can toggle them after creation.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowCreateModal(false); setNewRoleName(''); setNewRoleDesc(''); }}
                style={{
                  padding: '10px 20px', backgroundColor: '#4B5563', color: 'white',
                  border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRole}
                disabled={!newRoleName.trim() || saving}
                style={{
                  padding: '10px 20px', backgroundColor: !newRoleName.trim() ? '#374151' : '#10B981',
                  color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px',
                  fontWeight: '600', cursor: newRoleName.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {saving ? 'Creating...' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesAccess;
